/**
 * Jewelpa Custom Card Carousel
 * Native CSS scroll-snap with mouse drag-to-scroll and robust real-position arrow controls.
 * Zero external libraries, accessible keyboard navigation, responsive touch swipe.
 */

if (!customElements.get('custom-card-carousel')) {
  class CustomCardCarousel extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.prevBtn = null;
      this.nextBtn = null;

      this.isPointerDown = false;
      this.startX = 0;
      this.initialScrollLeft = 0;
      this.hasDragged = false;

      this.scrollTimeout = null;
      this.rafId = null;

      // Bound handlers for add/removeEventListener
      this.onScroll = this.onScroll.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.scrollPrev = this.scrollPrev.bind(this);
      this.scrollNext = this.scrollNext.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');

      if (!this.track) return;

      // Click advance on arrow buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', this.scrollPrev);
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', this.scrollNext);
      }

      // Scroll listener for real-position disabled states
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', () => this.updateArrowState(), { passive: true });
      }

      // Mouse drag-to-scroll
      this.track.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);

      // Keyboard navigation
      this.track.addEventListener('keydown', this.onKeyDown);

      // Resize / orientation handling
      window.addEventListener('resize', this.onResize, { passive: true });

      // Theme editor integration: scroll to selected block
      document.addEventListener('shopify:block:select', (event) => {
        const blockId = event.detail?.blockId;
        if (!blockId) return;
        const card = this.querySelector(`[data-block-id="${blockId}"]`);
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        }
      });

      // Initial state sync after rendering settles
      requestAnimationFrame(() => this.updateArrowState());
      setTimeout(() => this.updateArrowState(), 100);
    }

    disconnectedCallback() {
      if (this.prevBtn) {
        this.prevBtn.removeEventListener('click', this.scrollPrev);
      }
      if (this.nextBtn) {
        this.nextBtn.removeEventListener('click', this.scrollNext);
      }
      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('pointerdown', this.onPointerDown);
        this.track.removeEventListener('keydown', this.onKeyDown);
      }
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      window.removeEventListener('resize', this.onResize);
    }

    /**
     * Calculates horizontal advance distance for one card + gap
     * @returns {number}
     */
    getScrollStep() {
      if (!this.track) return 300;
      const card = this.track.querySelector('.custom-card-carousel__card');
      if (!card) return this.track.clientWidth * 0.8;
      const cardWidth = card.getBoundingClientRect().width;
      const style = window.getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap) || 24;
      return cardWidth + gap;
    }

    scrollPrev() {
      if (!this.track) return;
      const step = this.getScrollStep();
      this.track.scrollBy({ left: -step, behavior: 'smooth' });
      // Settle fallback check
      setTimeout(() => this.updateArrowState(), 350);
    }

    scrollNext() {
      if (!this.track) return;
      const step = this.getScrollStep();
      this.track.scrollBy({ left: step, behavior: 'smooth' });
      // Settle fallback check
      setTimeout(() => this.updateArrowState(), 350);
    }

    /**
     * Synchronizes arrow disabled state directly from the DOM's actual scroll metrics.
     * Uses 2px tolerance to avoid sub-pixel rounding false states.
     */
    updateArrowState() {
      if (!this.track) return;

      const scrollLeft = this.track.scrollLeft;
      const scrollWidth = this.track.scrollWidth;
      const clientWidth = this.track.clientWidth;
      const maxScrollLeft = scrollWidth - clientWidth;

      // 2px tolerance for fractional sub-pixel coordinates
      const isAtStart = scrollLeft <= 2;
      const isAtEnd = scrollLeft >= maxScrollLeft - 2;

      if (this.prevBtn) {
        this.prevBtn.disabled = isAtStart;
        this.prevBtn.setAttribute('aria-disabled', isAtStart ? 'true' : 'false');
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = isAtEnd;
        this.nextBtn.setAttribute('aria-disabled', isAtEnd ? 'true' : 'false');
      }
    }

    onScroll() {
      // Immediate RAF update for responsive button states
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.updateArrowState());

      // Debounced settle check
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => this.updateArrowState(), 60);
    }

    onResize() {
      this.updateArrowState();
    }

    onKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollNext();
      }
    }

    /**
     * Mouse drag-to-scroll handlers
     */
    onPointerDown(event) {
      // Only drag with primary mouse button
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.isPointerDown = true;
      this.startX = event.pageX;
      this.initialScrollLeft = this.track.scrollLeft;
      this.hasDragged = false;
    }

    onPointerMove(event) {
      if (!this.isPointerDown || !this.track) return;

      const deltaX = event.pageX - this.startX;

      if (!this.hasDragged && Math.abs(deltaX) > 4) {
        this.hasDragged = true;
        this.track.classList.add('is-dragging');
      }

      if (this.hasDragged) {
        event.preventDefault();
        this.track.scrollLeft = this.initialScrollLeft - deltaX;
        this.updateArrowState();
      }
    }

    onPointerUp() {
      if (!this.isPointerDown) return;
      this.isPointerDown = false;

      if (this.hasDragged && this.track) {
        this.track.classList.remove('is-dragging');

        // Prevent unintentional click on nested links after dragging
        const captureClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };
        this.track.addEventListener('click', captureClick, { capture: true, once: true });
        setTimeout(() => {
          this.track.removeEventListener('click', captureClick, { capture: true });
        }, 120);

        this.updateArrowState();
      }
    }
  }

  customElements.define('custom-card-carousel', CustomCardCarousel);
}
